import { call, put, select } from 'redux-saga/effects';

import { updateAppointmentRequested } from 'connect/actions';
import { NPR_ERROR_TYPES } from 'connect/constants';
import { extractErrorMessage, sendToLogger } from 'connect/saga/helpers';
import { apiRequestAppointment } from 'endpoints/patient-endpoints';

import { SagaHandler } from '../SagaHandler';

const getErrorType = (errorMessage) => {
  const errorTypesToApiErrorMap = {
    [NPR_ERROR_TYPES.SLOT_TAKEN]:
      'This reserved appointment slot is not available.',
    [NPR_ERROR_TYPES.APPOINTMENT_REQUESTED_ALREADY]:
      'You have already submitted an appointment request with this clinic.',
    [NPR_ERROR_TYPES.NOT_PATIENT]:
      'This patient could not be found.',
    [NPR_ERROR_TYPES.PROVIDER_NO_ONLINE_BOOKING]:
      'This provider does not have online appointment requests enabled.',
    [NPR_ERROR_TYPES.PROVIDER_NO_RESERVED_SLOTS]:
      'This provider does not have reserved slot booking enabled.',
    [NPR_ERROR_TYPES.PHONE_NOT_VERIFIED]:
      'Appointment creation failed: patient phone is not verified.',
    [NPR_ERROR_TYPES.EMAIL_NOT_VERIFIED]:
      'Appointment creation failed: patient email is not verified.',
  };

  const errorTypeIndex = Object.values(errorTypesToApiErrorMap).findIndex(
    (errorType) => errorType === errorMessage,
  );

  if (errorTypeIndex === -1) {
    return NPR_ERROR_TYPES.GENERIC;
  }

  const errorType = Object.keys(errorTypesToApiErrorMap)[errorTypeIndex];

  return errorType;
};

class _RequestAppointment extends SagaHandler {
  constructor() {
    super({
      actionName: 'appointment/REQUEST_APPOINTMENT',
      stateKey: 'requestAppointment',
    });
  }

  createAction(payload) {
    return {
      type: this._actionName,
      payload,
    };
  }

  * saga({ payload }) {
    const errorHandler = this.error;
    const loadingHandler = this.loading;
    const successHandler = this.success;
    const valueHandler = this.value;

    const isLoading = yield select(loadingHandler.selector);
    if (isLoading) {
      return;
    }

    yield put(loadingHandler.update(true));
    yield put(errorHandler.update(null));
    yield put(successHandler.update(null));

    try {
      const appointment = yield call(apiRequestAppointment, payload);
      yield put(valueHandler.update(appointment));
      // Keep legacy action for backwards compatibility
      yield put(updateAppointmentRequested(appointment));
      yield put(successHandler.update(true));
    } catch (error) {
      const errorMessage = extractErrorMessage(error);
      const errorType = getErrorType(errorMessage);
      yield put(errorHandler.update(errorType));
      sendToLogger(error, 'RequestAppointment');
    } finally {
      yield put(loadingHandler.update(false));
    }
  }
}

export const RequestAppointment = new _RequestAppointment();
